import { PostLoginResponse, PostLoginResponseData } from "@/interface/api/postLogin/types";
import { PostRegisterResponse, PostRegisterResponseData } from "@/interface/api/postRegister/types";
import { type Toast as HotToast } from "react-hot-toast";

export const SSO_PROVIDER = {
  GOOGLE: "google",
  APPLE: "apple",
  MICROSOFT: "microsoft",
} as const;

export type SsoProviderId = (typeof SSO_PROVIDER)[keyof typeof SSO_PROVIDER];

/** Optional metadata when SSO register/login completes (e.g. Apple `id_token` for GTM). */
export interface SsoCompletedMeta {
  registerSsoToken?: string;
}

export type SsoCompletedCallback = (
  provider: SsoProviderId,
  payload: unknown,
  meta?: SsoCompletedMeta
) => void;

export interface SSOAuthContainerProps {
  mode: "signin" | "signup";
  providers?: SsoProviderId[];
  rounded?: boolean;
  isFormLoading?: boolean;
  labels: Record<SsoProviderId, { label: string; loading: string }>;
  onSsoCompleted?: SsoCompletedCallback;
  onSsoLoadingChange?: (isLoading: boolean) => void;
  errors?: {
    loginError?: string;
    registerError?: string;
  };
}

export interface SSOButtonProps {
  provider: SsoProviderId;
  label: string;
  loadingLabel: string;
  isShowingLoadingLabel: boolean;
  isSsoLocked: boolean;
  isFormLoading?: boolean;
  rounded?: boolean;
  onClick: () => void;
  type?: "button" | "submit" | "reset";
  ariaLabel?: string;
  ariaBusy?: boolean;
}

export interface UseSSOAuthResult {
  isSsoLoading: boolean;
  activeProvider: SsoProviderId | null;
  accessToken: string | undefined;
  setSsoLoading: (isLoading: boolean, provider?: SsoProviderId | null) => void;
  setAccessToken: (token: string | undefined) => void;
  resetAllAuthState: (authHooks: SSOAuthLogicResult[]) => void;
}

export interface SSOSuccessHandlerOptions {
  onSaveCookie: (token: string, expiration: string) => void;
  onSsoCompleted?: SsoCompletedCallback;
  provider: SsoProviderId;
  /** Google access token or Apple `id_token`, used for GTM on register (Apple JWT decode). */
  registerSsoCredential?: string;
}
export interface SSOSuccessHandlerResult {
  handleLoginSuccess: (response: PostLoginResponse) => void;
  handleRegisterSuccess: (response: PostRegisterResponse) => void;
}

export interface SSOAuthLogicOptions {
  provider: SsoProviderId | null;
  onSaveCookie: (token: string, expiration: string) => void;
  onSsoCompleted?: SsoCompletedCallback;
  accessToken?: string;
  errors?: {
    loginError?: string;
    registerError?: string;
  };
  setSsoLoading: (isLoading: boolean, provider?: SsoProviderId | null) => void;
}

export interface SSOAuthLogicResult {
  handleLogin: (accessToken: string) => void;
  handleRegister: (accessToken: string) => void;
  resetApiState: () => void;
  isLoading: boolean;
  loginData: PostLoginResponseData | undefined;
  registerData: PostRegisterResponseData | undefined;
  loginError: unknown;
  registerError: unknown;
}

export interface SSOErrorHandlerOptions {
  provider: SsoProviderId;
  errors?: {
    loginError?: string;
    registerError?: string;
  };
  setSsoLoading: (isLoading: boolean, provider?: SsoProviderId | null) => void;
}

export interface SSOErrorHandlerResult {
  handleLoginError: (error: unknown) => void;
  handleRegisterError: (error: unknown) => void;
}

export interface SsoAuthToastProps {
  toast?: HotToast;
  message: string;
}
